import { Link } from 'react-router'
import { cn } from '#app/utils/misc.tsx'
import { MorphIcon } from './morph-icon.tsx'

export type OyTint =
	| 'paard'
	| 'atelier'
	| 'uitje'
	| 'sociaal'
	| 'medisch'
	| 'regel'
	| 'agenda'
	| 'stal'
	| 'accent'
	| 'done'
	| 'warm'

/** Section/card: radius 20, hairline, no shadow (or a pastel fill, no border). */
export function OyCard({
	tint,
	as: Tag = 'div',
	className,
	children,
	...props
}: {
	tint?: OyTint
	as?: 'div' | 'section' | 'article' | 'li'
	className?: string
	id?: string
	style?: React.CSSProperties
	'aria-labelledby'?: string
	children: React.ReactNode
}) {
	return (
		<Tag
			className={cn('oy-card', tint && `oy-tint-${tint}`, className)}
			{...props}
		>
			{children}
		</Tag>
	)
}

/** One line per item, meta under it, the action on the right (radius 16). */
export function OyRow({
	icon,
	title,
	sub,
	trailing,
	tint,
	done,
	as: Tag = 'div',
	className,
}: {
	/** decorative glyph (emoji); hidden from assistive tech — the title is the name */
	icon?: React.ReactNode
	title: React.ReactNode
	sub?: React.ReactNode
	trailing?: React.ReactNode
	tint?: OyTint
	/** struck-through title (klaar of geweest) */
	done?: boolean
	as?: 'div' | 'li' | 'article'
	className?: string
}) {
	return (
		<Tag
			className={cn(
				'oy-row',
				tint && `oy-tint-${tint}`,
				done && 'done',
				className,
			)}
		>
			{icon ? (
				<span className="oy-row-ico" aria-hidden="true">
					{icon}
				</span>
			) : null}
			<div className="oy-row-body">
				<p className="oy-row-title">{title}</p>
				{sub ? <p className="oy-row-sub">{sub}</p> : null}
			</div>
			{trailing ? <span className="oy-row-trail">{trailing}</span> : null}
		</Tag>
	)
}

/** Settings-style row: icon tile, title + sub, optional value, chevron ›. */
export function OyMenuRow({
	icon,
	title,
	sub,
	value,
	to,
	onClick,
	tile,
	className,
}: {
	icon: React.ReactNode
	title: React.ReactNode
	sub?: React.ReactNode
	value?: React.ReactNode
	to?: string
	onClick?: () => void
	/** override the tile's background (a pastel, accent-soft …) */
	tile?: string
	className?: string
}) {
	const body = (
		<>
			<span
				className="oy-tile"
				aria-hidden="true"
				style={tile ? { background: tile } : undefined}
			>
				{icon}
			</span>
			<span className="oy-menurow-body">
				<span className="oy-menurow-title">{title}</span>
				{sub ? <span className="oy-menurow-sub">{sub}</span> : null}
			</span>
			{value ? <span className="oy-menurow-value">{value}</span> : null}
			<MorphIcon state="chevR" className="oy-menurow-chev" />
		</>
	)
	if (to) {
		return (
			<Link to={to} className={cn('oy-menurow', className)}>
				{body}
			</Link>
		)
	}
	return (
		<button
			type="button"
			onClick={onClick}
			className={cn('oy-menurow', className)}
		>
			{body}
		</button>
	)
}

/** A page section: 18/800 heading, optional meta, a 10px grid of children. */
export function OySection({
	title,
	meta,
	action,
	className,
	children,
}: {
	title?: React.ReactNode
	meta?: React.ReactNode
	/** trailing control in the head (a quiet button) */
	action?: React.ReactNode
	className?: string
	children: React.ReactNode
}) {
	return (
		<section className={cn('oy-section', className)}>
			{title ? (
				<div className="oy-section-head">
					<h2>{title}</h2>
					{meta ? <span className="oy-section-meta">{meta}</span> : null}
					{action}
				</div>
			) : null}
			<div className="oy-section-body">{children}</div>
		</section>
	)
}

/**
 * The page title (30/800), with the date line above it. A detail page passes
 * `back` and gets the 40px round ‹ button left of a 26px title.
 */
export function OyPageTitle({
	kicker,
	title,
	back,
	className,
}: {
	kicker?: React.ReactNode
	title: React.ReactNode
	back?: { label: string } & ({ to: string } | { onClick: () => void })
	className?: string
}) {
	if (back) {
		return (
			<div className={cn('oy-pagetitle has-back', className)}>
				{'to' in back ? (
					<Link to={back.to} className="oy-back" aria-label={back.label}>
						<MorphIcon state="back" />
					</Link>
				) : (
					<button
						type="button"
						className="oy-back"
						aria-label={back.label}
						onClick={back.onClick}
					>
						<MorphIcon state="back" />
					</button>
				)}
				<div>
					{kicker ? <p className="oy-kicker">{kicker}</p> : null}
					<h1>{title}</h1>
				</div>
			</div>
		)
	}
	return (
		<div className={cn('oy-pagetitle', className)}>
			{kicker ? <p className="oy-kicker">{kicker}</p> : null}
			<h1>{title}</h1>
		</div>
	)
}

/** The dashed "vrij" cell. */
export function OyEmpty({
	children = 'vrij',
	className,
}: {
	children?: React.ReactNode
	className?: string
}) {
	return <div className={cn('oy-empty', className)}>{children}</div>
}

/**
 * Thin progress bar (inzicht widget) or the stal tally bar with a target
 * tick ("telt voor"). `tone` is a named tone or a hex (a person's hue).
 */
export function OyProgressBar({
	pct,
	tone = 'done',
	target,
	tally,
	label,
	className,
}: {
	pct: number
	tone?: 'done' | 'accent' | 'warm' | (string & {})
	/** target percentage, drawn as a 2px tick */
	target?: number
	tally?: boolean
	/** accessible name; omit when a visible text next to it carries the value */
	label?: string
	className?: string
}) {
	const named = tone === 'done' || tone === 'accent' || tone === 'warm'
	const width = `${Math.max(0, Math.min(100, pct))}%`
	return (
		<span
			className={cn('oy-progress', tally && 'tally', className)}
			role={label ? 'progressbar' : undefined}
			aria-label={label}
			aria-valuenow={label ? Math.round(pct) : undefined}
			aria-valuemin={label ? 0 : undefined}
			aria-valuemax={label ? 100 : undefined}
		>
			<span
				className={cn('oy-progress-bar', named && `tone-${tone}`)}
				style={named ? { width } : { width, background: tone }}
			/>
			{target != null ? (
				<span className="oy-progress-tick" style={{ left: `${target}%` }} />
			) : null}
		</span>
	)
}
